/**
 * SellerFinancingComparison Component (Phase 50.1-03)
 *
 * Displays side-by-side comparison of bank vs seller financing with:
 * - Clear "you win" indicators (green check / red X)
 * - Three rate scenarios for negotiation planning
 * - Break-even rate callout
 * - Negotiation strategy tip
 *
 * Uses luxury dark theme (charcoal, gold, emerald).
 */

import React from 'react';
import { Check, X, TrendingUp, Target, AlertTriangle, Lightbulb, Scale } from 'lucide-react';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import type { ExtendedFinancingInfo, RateScenario } from '../../types/calculations';

// =============================================================================
// Types
// =============================================================================

// Re-export RateScenario for backwards compatibility
export type { RateScenario };

/** Comparison delta metrics */
export interface FinancingComparisonDeltas {
  monthly_payment_delta: number;      // Seller - conventional (negative = seller wins)
  annual_debt_service_delta: number;  // Seller - conventional
  equity_required_delta: number;      // Seller - conventional (negative = seller wins)
  coc_delta: number;                  // Seller - conventional (positive = seller wins)
  break_even_rate: number;            // Rate where seller equals bank CoC
}

export interface SellerFinancingComparisonProps {
  conventional: ExtendedFinancingInfo;
  seller: ExtendedFinancingInfo;
  comparison: FinancingComparisonDeltas;
  scenarios: RateScenario[];
  recommendedRate?: number;           // Recommended starting negotiation rate
  className?: string;
}

// =============================================================================
// Helper Components
// =============================================================================

/** Win/Lose indicator with color coding */
const WinIndicator: React.FC<{ isWin: boolean; neutral?: boolean }> = ({
  isWin,
  neutral = false,
}) => {
  if (neutral) {
    return <span className="text-stone-700">-</span>;
  }
  return isWin ? (
    <Check className="w-4 h-4 text-emerald" />
  ) : (
    <X className="w-4 h-4 text-crimson" />
  );
};

/** Format delta with sign and color */
const DeltaCell: React.FC<{
  value: number;
  isCurrency?: boolean;
  isPercent?: boolean;
  invertColor?: boolean; // For cases where positive is bad
}> = ({ value, isCurrency = false, isPercent = false, invertColor = false }) => {
  const isPositive = value > 0;
  const isNegative = value < 0;

  // Determine color: by default, negative delta = green (seller wins), positive = red
  // invertColor flips this for metrics like CoC where positive is good
  const colorClass = invertColor
    ? isPositive
      ? 'text-emerald'
      : isNegative
      ? 'text-crimson'
      : 'text-stone-700'
    : isNegative
    ? 'text-emerald'
    : isPositive
    ? 'text-crimson'
    : 'text-stone-700';

  let displayValue: string;
  if (isCurrency) {
    displayValue = `${isPositive ? '+' : ''}${formatCurrency(value)}`;
  } else if (isPercent) {
    // Value is already in decimal form (0.059 = 5.9%)
    displayValue = `${isPositive ? '+' : ''}${(value * 100).toFixed(1)}%`;
  } else {
    displayValue = `${isPositive ? '+' : ''}${value.toFixed(0)}`;
  }

  return <span className={`font-mono tabular-nums ${colorClass}`}>{displayValue}</span>;
};

/** Scenario card for rate negotiation */
const ScenarioCard: React.FC<{
  scenario: RateScenario;
  isRecommended?: boolean;
}> = ({ scenario, isRecommended = false }) => {
  // Map labels to styling
  const labelStyles: Record<string, { bg: string; border: string; text: string }> = {
    Aggressive: {
      bg: 'bg-emerald/10',
      border: 'border-emerald/30',
      text: 'text-emerald',
    },
    Moderate: {
      bg: 'bg-gold/10',
      border: 'border-gold/30',
      text: 'text-gold',
    },
    Conservative: {
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-400',
    },
  };

  const style = labelStyles[scenario.label] || labelStyles.Moderate;
  const savingsVsBank = Math.abs(scenario.savings_vs_bank);
  const isSaving = scenario.savings_vs_bank < 0;

  return (
    <div
      className={`
        relative rounded-lg p-4 border transition-all duration-200
        ${style.bg} ${style.border}
        ${isRecommended ? 'ring-2 ring-gold shadow-lg shadow-gold/20' : ''}
      `}
    >
      {isRecommended && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gold text-[#1c1917] text-sm font-bold uppercase tracking-wider rounded">
          Recommended
        </div>
      )}

      {/* Label & Rate */}
      <div className="flex items-center justify-between mb-3">
        <span className={`text-sm font-semibold uppercase tracking-wider ${style.text}`}>
          {scenario.label}
        </span>
        <span className="text-xl font-mono tabular-nums font-bold text-ivory">
          {(scenario.rate * 100).toFixed(1)}%
        </span>
      </div>

      {/* Metrics */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-stone-700">Monthly Payment</span>
          <span className="font-mono tabular-nums text-ivory">
            {formatCurrency(scenario.monthly_payment)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-700">Annual Debt Service</span>
          <span className="font-mono tabular-nums text-ivory">
            {formatCurrency(scenario.annual_debt_service)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-700">Cash-on-Cash</span>
          <span className="font-mono tabular-nums text-emerald font-semibold">
            {formatPercent(scenario.coc, 1)}
          </span>
        </div>

        {/* Savings vs Bank */}
        <div className="pt-2 mt-2 border-t border-white/10">
          <div className="flex justify-between items-center">
            <span className="text-stone-700 text-sm">vs Bank</span>
            <span
              className={`font-mono tabular-nums text-sm font-semibold ${
                isSaving ? 'text-emerald' : 'text-amber-400'
              }`}
            >
              {isSaving ? '-' : '+'}${savingsVsBank.toLocaleString()}/mo
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const SellerFinancingComparison: React.FC<SellerFinancingComparisonProps> = ({
  conventional,
  seller,
  comparison,
  scenarios,
  recommendedRate,
  className = '',
}) => {
  const { monthly_payment_delta, equity_required_delta, coc_delta, break_even_rate } = comparison;

  // Calculate monthly payment from annual debt service for display
  const bankMonthly = conventional.annual_debt_service / 12;
  const sellerMonthly = seller.annual_debt_service / 12;

  // Determine overall winner based on CoC (primary metric for investors)
  const sellerWinsOverall = coc_delta > 0;

  // Build comparison rows
  const comparisonRows = [
    {
      label: 'Down Payment',
      bank: conventional.equity_required,
      seller: seller.equity_required,
      delta: equity_required_delta,
      isCurrency: true,
      sellerWins: equity_required_delta < 0,
    },
    {
      label: 'Monthly Payment',
      bank: bankMonthly,
      seller: sellerMonthly,
      delta: monthly_payment_delta,
      isCurrency: true,
      sellerWins: monthly_payment_delta < 0,
    },
    {
      label: 'Annual Debt Service',
      bank: conventional.annual_debt_service,
      seller: seller.annual_debt_service,
      delta: comparison.annual_debt_service_delta,
      isCurrency: true,
      sellerWins: comparison.annual_debt_service_delta < 0,
    },
    {
      label: 'Cash-on-Cash',
      bank: conventional.dscr ? (conventional as any).coc || 0 : 0, // May need to be passed separately
      seller: (seller as any).coc || 0,
      delta: coc_delta,
      isPercent: true,
      sellerWins: coc_delta > 0,
      invertColor: true, // Positive CoC delta is good
    },
  ];

  return (
    <div className={`bg-charcoal rounded-lg border border-slate-dark ${className}`}>
      {/* Header */}
      <div className="p-5 border-b border-slate-dark">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scale className="w-5 h-5 text-gold" />
            <h3 className="text-lg font-serif text-ivory">Financing Comparison</h3>
          </div>
          {sellerWinsOverall && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald/20 rounded-full">
              <TrendingUp className="w-4 h-4 text-emerald" />
              <span className="text-sm font-semibold text-emerald">Seller Financing Wins</span>
            </div>
          )}
        </div>
        <p className="text-sm text-stone-700 mt-1">
          Side-by-side analysis at current rates
        </p>
      </div>

      {/* Comparison Table */}
      <div className="p-5">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-sm uppercase tracking-wider text-stone-700">
                <th className="text-left py-2 font-medium">Metric</th>
                <th className="text-right py-2 font-medium">Bank</th>
                <th className="text-right py-2 font-medium">Seller</th>
                <th className="text-right py-2 font-medium">Delta</th>
                <th className="text-center py-2 font-medium w-12">Win</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-dark/50">
              {comparisonRows.map((row) => (
                <tr key={row.label} className="hover:bg-graphite/30 transition-colors">
                  <td className="py-3 text-sm text-stone-700">{row.label}</td>
                  <td className="py-3 text-right font-mono tabular-nums text-ivory">
                    {row.isCurrency
                      ? formatCurrency(row.bank)
                      : row.isPercent
                      ? formatPercent(row.bank, 1)
                      : row.bank.toFixed(2)}
                  </td>
                  <td className="py-3 text-right font-mono tabular-nums text-ivory">
                    {row.isCurrency
                      ? formatCurrency(row.seller)
                      : row.isPercent
                      ? formatPercent(row.seller, 1)
                      : row.seller.toFixed(2)}
                  </td>
                  <td className="py-3 text-right">
                    <DeltaCell
                      value={row.delta}
                      isCurrency={row.isCurrency}
                      isPercent={row.isPercent}
                      invertColor={row.invertColor}
                    />
                  </td>
                  <td className="py-3 text-center">
                    <div className="flex justify-center">
                      <WinIndicator isWin={row.sellerWins} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Break-Even Callout */}
      <div className="mx-5 mb-5 p-4 bg-gold/10 border border-gold/30 rounded-lg">
        <div className="flex items-start gap-3">
          <Target className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-gold uppercase tracking-wider">
                Break-Even Rate
              </span>
              <span className="text-2xl font-mono tabular-nums font-bold text-ivory">
                {(break_even_rate * 100).toFixed(1)}%
              </span>
            </div>
            <p className="text-sm text-stone-700 mt-1">
              At this rate, monthly payment equals bank financing. Above this rate, you're better
              off with the bank.
            </p>
          </div>
        </div>
      </div>

      {/* Negotiation Scenarios */}
      <div className="border-t border-slate-dark p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-gold" />
          <h4 className="text-sm font-semibold text-ivory uppercase tracking-wider">
            Rate Scenarios
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {scenarios.map((scenario) => (
            <ScenarioCard
              key={scenario.label}
              scenario={scenario}
              isRecommended={
                recommendedRate !== undefined &&
                Math.abs(scenario.rate - recommendedRate) < 0.001
              }
            />
          ))}
        </div>
      </div>

      {/* Negotiation Strategy Tip */}
      <div className="mx-5 mb-5 p-4 bg-emerald/10 border border-emerald/30 rounded-lg">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-sm font-semibold text-emerald uppercase tracking-wider">
              Negotiation Strategy
            </span>
            <p className="text-sm text-stone-700 mt-1">
              Start at{' '}
              <span className="font-mono tabular-nums font-semibold text-emerald">
                {((recommendedRate || scenarios[0]?.rate || 0.05) * 100).toFixed(1)}%
              </span>
              . If the seller counters above{' '}
              <span className="font-mono tabular-nums font-semibold text-gold">
                {(break_even_rate * 100).toFixed(1)}%
              </span>
              , you're better off with bank financing.
            </p>
            <p className="text-sm text-stone-700 mt-2">
              Equity savings of {formatCurrency(Math.abs(equity_required_delta))} could fund{' '}
              {Math.floor(Math.abs(equity_required_delta) / conventional.equity_required)} additional
              down payments at conventional terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerFinancingComparison;
